import { customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './tags-list.html';
import { listTags, deleteTag, listTagGroups, deleteTagGroup, reorderTagGroups, reorderTagsInGroup, moveTagToGroup } from '../services/api.service';
import type { Tag, TagGroup } from '../services/api.types';
import { ConfirmDialog } from '../components/confirm-dialog';

export type TagTreeGroup = {
  group: TagGroup | null;
  tags: Tag[];
  expanded: boolean;
};

@customElement({ name: 'tags-list', template })
export class TagsList {
  private readonly i18n: I18N = resolve(I18N);

  public groups: TagGroup[] = [];
  public tags: Tag[] = [];
  public treeGroups: TagTreeGroup[] = [];
  public errorMessage = '';

  public showTagModal = false;
  public tagToEdit: Tag | null = null;
  public showGroupModal = false;
  public groupToEdit: TagGroup | null = null;

  public dragGroupSource: string | null = null;
  public dragGroupOver: string | null = null;
  public dragTagSource: string | null = null;
  public dragTagOver: string | null = null;

  public async loading(): Promise<void> {
    await this.fetchAll();
  }

  public async fetchAll(): Promise<void> {
    try {
      const [tags, groups] = await Promise.all([listTags('', false), listTagGroups()]);
      this.tags = tags;
      this.groups = groups;
      this.buildTree();
    } catch (e: unknown) {
      this.errorMessage = e instanceof Error ? e.message : String(e);
    }
  }

  public buildTree(): void {
    const previousExpanded = new Map(this.treeGroups.map(g => [g.group?.id ?? '__ungrouped__', g.expanded]));
    const result: TagTreeGroup[] = [];

    for (const group of this.groups) {
      const groupTags = this.tags.filter(t => t.groupId === group.id).sort((a, b) => a.position - b.position);
      result.push({
        group,
        tags: groupTags,
        expanded: previousExpanded.get(group.id) ?? true,
      });
    }

    const ungroupedTags = this.tags.filter(t => !t.groupId).sort((a, b) => a.position - b.position);
    if (ungroupedTags.length > 0 || result.length === 0) {
      result.push({
        group: null,
        tags: ungroupedTags,
        expanded: previousExpanded.get('__ungrouped__') ?? true,
      });
    }

    this.treeGroups = result;
  }

  public toggleGroup(group: TagTreeGroup): void {
    group.expanded = !group.expanded;
    this.treeGroups = [...this.treeGroups];
  }

  public expandAll(): void {
    this.treeGroups = this.treeGroups.map(group => ({ ...group, expanded: true }));
  }

  public collapseAll(): void {
    this.treeGroups = this.treeGroups.map(group => ({ ...group, expanded: false }));
  }

  public openCreateTagModal(): void {
    this.tagToEdit = null;
    this.showTagModal = true;
  }

  public openEditTagModal(tag: Tag): void {
    this.tagToEdit = tag;
    this.showTagModal = true;
  }

  public onTagModalSaved(tag: Tag): void {
    const idx = this.tags.findIndex(t => t.id === tag.id);
    if (idx >= 0) {
      this.tags = this.tags.map(t => t.id === tag.id ? tag : t);
    } else {
      this.tags = [...this.tags, tag];
    }
    this.showTagModal = false;
    this.tagToEdit = null;
    this.buildTree();
  }

  public onTagModalClosed(): void {
    this.showTagModal = false;
    this.tagToEdit = null;
  }

  public async removeTag(tag: Tag): Promise<void> {
    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('tags.delete.confirm', { name: tag.name }),
      this.i18n.tr('tags.delete.confirmTitle')
    );
    if (!confirmed) return;
    try {
      await deleteTag(tag.id);
      this.tags = this.tags.filter(t => t.id !== tag.id);
      this.buildTree();
    } catch (e: unknown) {
      this.errorMessage = e instanceof Error ? e.message : String(e);
    }
  }

  public openCreateGroupModal(): void {
    this.groupToEdit = null;
    this.showGroupModal = true;
  }

  public openEditGroupModal(group: TagGroup): void {
    this.groupToEdit = group;
    this.showGroupModal = true;
  }

  public onGroupModalSaved(group: TagGroup): void {
    const idx = this.groups.findIndex(g => g.id === group.id);
    if (idx >= 0) {
      this.groups = this.groups.map(g => g.id === group.id ? group : g);
    } else {
      this.groups = [...this.groups, group];
    }
    this.showGroupModal = false;
    this.groupToEdit = null;
    this.buildTree();
  }

  public onGroupModalClosed(): void {
    this.showGroupModal = false;
    this.groupToEdit = null;
  }

  public async removeGroup(group: TagGroup): Promise<void> {
    const tagCount = this.tags.filter(t => t.groupId === group.id).length;
    const message = tagCount > 0
      ? this.i18n.tr('tags.groups.deleteConfirmWithTags', { name: group.name, count: tagCount })
      : this.i18n.tr('tags.groups.deleteConfirm', { name: group.name });
    const confirmed = await ConfirmDialog.confirm(message, this.i18n.tr('tags.groups.deleteTitle'));
    if (!confirmed) return;
    try {
      await deleteTagGroup(group.id);
      this.groups = this.groups.filter(g => g.id !== group.id);
      this.tags = this.tags.map(t => t.groupId === group.id ? { ...t, groupId: undefined } : t);
      this.buildTree();
    } catch (e: unknown) {
      this.errorMessage = e instanceof Error ? e.message : String(e);
    }
  }

  public onGroupDragStart(e: DragEvent, groupId: string): void {
    this.dragGroupSource = groupId;
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  }

  public onGroupDragOver(e: DragEvent, groupId: string | null): void {
    // If a tag is being dragged, allow drop on the group header (for move-to-group)
    // but don't show group reorder highlight
    if (this.dragTagSource !== null) {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      return;
    }
    // Only allow group reorder drops on real groups (not ungrouped)
    if (!groupId) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    this.dragGroupOver = groupId;
  }

  public onGroupDragLeave(): void {
    this.dragGroupOver = null;
  }

  public async onGroupDrop(e: DragEvent, targetGroupId: string | null): Promise<void> {
    e.preventDefault();
    e.stopPropagation();

    // Tag dropped onto a group row → move it to this group
    if (this.dragTagSource !== null) {
      const tagId = this.dragTagSource;
      this.dragTagSource = null;
      this.dragTagOver = null;
      const sourceTag = this.tags.find(t => t.id === tagId);
      if (!sourceTag) return;
      if ((sourceTag.groupId ?? null) === targetGroupId) return;
      const targetGroupTags = this.treeGroups.find(g => (g.group?.id ?? null) === targetGroupId)?.tags ?? [];
      const newPosition = targetGroupTags.length;
      this.tags = this.tags.map(t => t.id === tagId ? { ...t, groupId: targetGroupId ?? undefined, position: newPosition } : t);
      this.buildTree();
      try {
        await moveTagToGroup(tagId, targetGroupId, newPosition);
        await this.fetchAll();
      } catch (err: unknown) {
        this.errorMessage = err instanceof Error ? err.message : String(err);
      }
      return;
    }

    // Group reorder — only for real groups
    if (!targetGroupId) return;
    const sourceId = this.dragGroupSource;
    this.dragGroupSource = null;
    this.dragGroupOver = null;

    if (!sourceId || sourceId === targetGroupId) return;

    const reordered = [...this.groups];
    const fromIdx = reordered.findIndex(g => g.id === sourceId);
    const toIdx = reordered.findIndex(g => g.id === targetGroupId);
    if (fromIdx < 0 || toIdx < 0) return;

    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    this.groups = reordered;
    this.buildTree();

    try {
      await reorderTagGroups(reordered.map(g => g.id));
    } catch (err: unknown) {
      this.errorMessage = err instanceof Error ? err.message : String(err);
    }
  }

  public onGroupDragEnd(): void {
    this.dragGroupSource = null;
    this.dragGroupOver = null;
  }

  public onTagDragStart(e: DragEvent, tagId: string): void {
    this.dragTagSource = tagId;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', tagId);
    }
    e.stopPropagation();
  }

  public onTagDragOver(e: DragEvent, tagId: string): void {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    this.dragTagOver = tagId;
  }

  public onTagDragLeave(): void {
    this.dragTagOver = null;
  }

  public async onTagDrop(e: DragEvent, targetTagId: string, targetGroupId: string | null): Promise<void> {
    e.preventDefault();
    e.stopPropagation();
    const sourceTagId = this.dragTagSource;
    this.dragTagSource = null;
    this.dragTagOver = null;

    if (!sourceTagId || sourceTagId === targetTagId) return;

    const sourceTag = this.tags.find(t => t.id === sourceTagId);
    if (!sourceTag) return;

    const sameGroup = (sourceTag.groupId ?? null) === targetGroupId;

    if (sameGroup) {
      const groupTags = this.treeGroups.find(group => (group.group?.id ?? null) === targetGroupId)?.tags ?? [];
      const reordered = [...groupTags];
      const fromIdx = reordered.findIndex(t => t.id === sourceTagId);
      const toIdx = reordered.findIndex(t => t.id === targetTagId);
      if (fromIdx < 0 || toIdx < 0) return;
      const [moved] = reordered.splice(fromIdx, 1);
      reordered.splice(toIdx, 0, moved);
      this.tags = this.tags.map(t => {
        const idx = reordered.findIndex(rt => rt.id === t.id);
        return idx >= 0 ? { ...t, position: idx } : t;
      });
      this.buildTree();
      try {
        await reorderTagsInGroup(reordered.map(t => t.id));
      } catch (err: unknown) {
        this.errorMessage = err instanceof Error ? err.message : String(err);
      }
    } else {
      const targetGroupTags = this.treeGroups.find(group => (group.group?.id ?? null) === targetGroupId)?.tags ?? [];
      const toIdx = targetGroupTags.findIndex(t => t.id === targetTagId);
      const newPosition = toIdx >= 0 ? toIdx : targetGroupTags.length;
      this.tags = this.tags.map(t => t.id === sourceTagId ? { ...t, groupId: targetGroupId ?? undefined, position: newPosition } : t);
      this.buildTree();
      try {
        const moved = await moveTagToGroup(sourceTagId, targetGroupId, newPosition);
        this.tags = this.tags.map(t => t.id === sourceTagId ? moved : t);
        await this.fetchAll();
      } catch (err: unknown) {
        this.errorMessage = err instanceof Error ? err.message : String(err);
      }
    }
  }

  public onTagDragEnd(): void {
    this.dragTagSource = null;
    this.dragTagOver = null;
  }
}

